// Admin theme — lets admins recolor the backend (primary / secondary / button /
// toast / text) and choose whether the left sidebar starts collapsed. Persisted
// to localStorage and applied as CSS variables on :root, so anything that reads
// --color-burgundy / --color-gold / --theme-* (and react-toastify's dark var)
// re-themes live.

export const THEME_KEY = 'mm_admin_theme';

export const DEFAULT_THEME = {
  primary: '#722F37',   // 主色 — maps to --color-burgundy (links, headers, primary buttons)
  secondary: '#C9A227', // 辅色 — maps to --color-gold (accents)
  button: '#722F37',    // 按钮主色
  toast: '#2C2C2C',     // Toastify 背景色（dark 主题）
  text: '#2C2C2C',      // 正文文字色
  background: '#FDF8F3', // 页面背景
  card: '#FFFFFF',      // 卡片背景
  panel: '#FFFFFF',     // 面板背景
  badge: '#457B9D',     // 徽章背景（通用 badge）
  table: '#FAFAF8',     // 表头背景
  title: '#2C2C2C',     // 标题颜色
  subtitle: '#6B6B6B',  // 副标题颜色
  tabs: '#722F37',      // 选中标签颜色
  // Button styling (applies to .btn / .btn-primary classes).
  buttonText: '#FFFFFF',     // 按钮文字色
  buttonBorderColor: '#722F37', // 按钮边框色
  buttonBorderWidth: 0,      // 按钮边框宽度 (px)
  buttonRadius: 8,           // 按钮圆角 (px)
  buttonSize: 'medium',      // 按钮大小 small | medium | large
  buttonHoverBg: '#5A252C',  // 主按钮 hover 背景
  buttonHoverText: '#FFFFFF',// 主按钮 hover 文字
  badgeText: '#FFFFFF',      // 徽章文字色（通用 badge）
  badgeRadius: 999,          // 徽章圆角 (px)
  toggleColor: '#111111',    // 开关(toggle) 开启颜色
  // Icon button (.btn-icon) — base / hover / focus, bg + text.
  btnIconBg: '#FFFFFF',        btnIconText: '#2C2C2C',
  btnIconHoverBg: '#FDF8F3',   btnIconHoverText: '#2C2C2C',
  btnIconFocusBg: '#FDF8F3',   btnIconFocusText: '#2C2C2C',
  sidebarCollapsedDefault: false, // 左侧菜单默认是否收起/隐藏
};

// Button size presets → [padding, font-size].
export const BUTTON_SIZES = {
  small:  ['6px 14px',  '0.8rem'],
  medium: ['10px 20px', '0.9rem'],
  large:  ['14px 28px', '1rem'],
};

function clamp(n) { return Math.max(0, Math.min(255, n)); }

// Lighten (pct > 0) or darken (pct < 0) a hex color by a percentage.
function shade(hex, pct) {
  try {
    let h = String(hex || '').replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const num = parseInt(h, 16);
    let r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
    const f = pct / 100;
    const adj = (c) => clamp(Math.round(c + (f < 0 ? c * f : (255 - c) * f)));
    return '#' + [adj(r), adj(g), adj(b)].map(x => x.toString(16).padStart(2, '0')).join('');
  } catch { return hex; }
}

export function loadTheme() {
  try { return { ...DEFAULT_THEME, ...(JSON.parse(localStorage.getItem(THEME_KEY) || '{}')) }; }
  catch { return { ...DEFAULT_THEME }; }
}

export function applyTheme(t) {
  if (typeof document === 'undefined') return;
  const th = { ...DEFAULT_THEME, ...(t || {}) };
  const s = document.documentElement.style;
  // Primary → burgundy family (used widely across the app CSS).
  s.setProperty('--color-burgundy', th.primary);
  s.setProperty('--color-burgundy-dark', shade(th.primary, -15));
  s.setProperty('--color-burgundy-light', shade(th.primary, 18));
  // Secondary → gold family.
  s.setProperty('--color-gold', th.secondary);
  s.setProperty('--color-gold-dark', shade(th.secondary, -15));
  s.setProperty('--color-gold-light', shade(th.secondary, 18));
  // Dedicated theme vars (button / text / toast).
  s.setProperty('--theme-primary', th.primary);
  s.setProperty('--theme-secondary', th.secondary);
  s.setProperty('--theme-button', th.button);
  s.setProperty('--theme-button-dark', shade(th.button, -15));
  s.setProperty('--theme-text', th.text);
  s.setProperty('--theme-toast', th.toast);
  // Surface / element vars used by the shared CSS classes (best-effort: only
  // class-based elements follow these; inline-styled components keep their own
  // colors).
  s.setProperty('--theme-bg', th.background);
  s.setProperty('--theme-card', th.card);
  s.setProperty('--theme-panel', th.panel);
  s.setProperty('--theme-badge', th.badge);
  s.setProperty('--theme-table', th.table);
  s.setProperty('--theme-title', th.title);
  s.setProperty('--theme-subtitle', th.subtitle);
  s.setProperty('--theme-tabs', th.tabs);
  // Button styling.
  s.setProperty('--theme-button-text', th.buttonText);
  s.setProperty('--theme-btn-radius', (Number(th.buttonRadius) || 0) + 'px');
  const bw = Number(th.buttonBorderWidth) || 0;
  s.setProperty('--theme-btn-border', bw > 0 ? `${bw}px solid ${th.buttonBorderColor || th.button}` : 'none');
  const [pad, font] = BUTTON_SIZES[th.buttonSize] || BUTTON_SIZES.medium;
  s.setProperty('--theme-btn-pad', pad);
  s.setProperty('--theme-btn-font', font);
  s.setProperty('--theme-btn-hover-bg', th.buttonHoverBg);
  s.setProperty('--theme-btn-hover-text', th.buttonHoverText);
  s.setProperty('--theme-badge-text', th.badgeText);
  s.setProperty('--theme-badge-radius', (Number(th.badgeRadius) || 0) + 'px');
  s.setProperty('--theme-toggle', th.toggleColor);
  s.setProperty('--theme-btnicon-bg', th.btnIconBg);
  s.setProperty('--theme-btnicon-text', th.btnIconText);
  s.setProperty('--theme-btnicon-hover-bg', th.btnIconHoverBg);
  s.setProperty('--theme-btnicon-hover-text', th.btnIconHoverText);
  s.setProperty('--theme-btnicon-focus-bg', th.btnIconFocusBg);
  s.setProperty('--theme-btnicon-focus-text', th.btnIconFocusText);
  // react-toastify dark theme background.
  s.setProperty('--toastify-color-dark', th.toast);
}

export function saveTheme(t) {
  try { localStorage.setItem(THEME_KEY, JSON.stringify(t)); } catch (_) { /* ignore */ }
  applyTheme(t);
  try { window.dispatchEvent(new Event('mm-themechange')); } catch (_) { /* ignore */ }
}

// Fetch the GLOBAL theme saved by the admin (platform_settings) and apply it
// for every user/device. Cached to localStorage so the next first-paint is
// instant. Public endpoint — works for all roles, logged in or not.
export async function syncRemoteTheme() {
  try {
    const r = await fetch('/api/public/theme', { credentials: 'omit' });
    if (!r.ok) return;
    const d = await r.json();
    if (d && d.theme && Object.keys(d.theme).length) {
      const merged = { ...DEFAULT_THEME, ...d.theme };
      try { localStorage.setItem(THEME_KEY, JSON.stringify(merged)); } catch (_) { /* ignore */ }
      applyTheme(merged);
      try { window.dispatchEvent(new Event('mm-themechange')); } catch (_) { /* ignore */ }
    }
  } catch (_) { /* offline / not configured — keep local */ }
}
